import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import OnboardingAPI from '../../api/onboarding';

let createBotInFlight = null;

export const state = {
  currentStep: null,
  data: {
    selectActivity: {
      account_name: null,
      activity_area: null,
      store_types: null,
    },
    createBot: {
      brand_url: null,
    },
    knowledgeSources: {
      has_corpora: false,
    },
    reviewInfo: {
      brand_color: null,
      brand_name: null,
      bot_name: null,
    },
    testBot: {
      questions: [],
    },
  },
  uiFlags: {
    isInitialized: false,
    isFetchingStep: false,
    isFetching: false,
    isUpdating: false,
    isCompleted: false,
  },
};

export const getters = {
  getCurrentStep: $state => $state.currentStep,
  getStepData: $state => stepName => $state.data[stepName],
  getUIFlags: $state => $state.uiFlags,
  isCompleted: $state => $state.uiFlags.isCompleted,
  getSelectActivityData: $state => $state.data.selectActivity,
  getCreateBotData: $state => $state.data.createBot,
  getKnowledgeSourcesData: $state => $state.data.knowledgeSources,
  getReviewInfoData: $state => $state.data.reviewInfo,
  getTestBotData: $state => $state.data.testBot,
};

export const actions = {
  fetchCurrentStep: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetchingStep: true });
    try {
      const response = await OnboardingAPI.getCurrentStep();
      commit(types.SET_ONBOARDING_STEP, response.data.last_step);
      commit(types.SET_ONBOARDING_UI_FLAG, {
        isFetchingStep: false,
        isCompleted: response.data.completed,
        isInitialized: true,
      });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetchingStep: false });
      throw error;
    }
  },

  fetchSelectActivity: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: true });
    try {
      const response = await OnboardingAPI.getSelectActivity();
      commit(types.SET_ONBOARDING_DATA, {
        step: 'selectActivity',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      throw error;
    }
  },

  updateSelectActivity: async ({ commit }, data) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
    try {
      const payload = {
        account_name: data.account_name,
        activity_area: data.activity_area,
        store_types: data.store_types,
        last_step: 'select_activity',
      };
      const response = await OnboardingAPI.updateSelectActivity(payload);
      commit(types.SET_ONBOARDING_DATA, {
        step: 'selectActivity',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_STEP, 'select_activity');
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },

  fetchCreateBot: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: true });
    try {
      const response = await OnboardingAPI.getCreateBot();
      commit(types.SET_ONBOARDING_DATA, {
        step: 'createBot',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      throw error;
    }
  },

  updateCreateBot: async ({ commit }, data) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
    try {
      const response = await OnboardingAPI.updateCreateBot({
        brand_url: data.brand_url,
      });
      commit(types.SET_ONBOARDING_DATA, {
        step: 'createBot',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_STEP, 'create_bot');
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },
  updateCreateBotWithCorpora: async (
    { commit, dispatch, rootGetters },
    { brand_url }
  ) => {
    if (createBotInFlight) {
      return createBotInFlight;
    }

    const isInsideAccountScopedURLs =
      window.location.pathname.includes('/app/accounts');
    const accountId = isInsideAccountScopedURLs
      ? window.location.pathname.split('/')[3]
      : null;
    const localStorageKey = accountId
      ? `bot_create_inflight_${accountId}`
      : null;

    if (localStorageKey && localStorage.getItem(localStorageKey)) {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        await dispatch('newBotify/get', null, { root: true });
        const isBotCreated = rootGetters['newBotify/getIsCreated'];
        if (isBotCreated) {
          const response = await OnboardingAPI.updateCreateBot({ brand_url });
          commit(types.SET_ONBOARDING_DATA, {
            step: 'createBot',
            data: response.data,
          });
          commit(types.SET_ONBOARDING_STEP, 'create_bot');

          if (brand_url) {
            try {
              await dispatch(
                'newBotify/createCorpora',
                { url: brand_url },
                { root: true }
              );
            } catch {
            }
          }

          await dispatch('newBotify/get', null, { root: true });
          if (localStorageKey) {
            localStorage.removeItem(localStorageKey);
          }
          commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
          return { success: true };
        }
      } catch {
      }
    }

    const promise = (async () => {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, 'true');
      }

      try {
        const response = await OnboardingAPI.updateCreateBot({ brand_url });
        commit(types.SET_ONBOARDING_DATA, {
          step: 'createBot',
          data: response.data,
        });
        commit(types.SET_ONBOARDING_STEP, 'create_bot');

        try {
          await dispatch('newBotify/get', null, { root: true });
        } catch {
        }

        const isBotCreated = rootGetters['newBotify/getIsCreated'];
        if (!isBotCreated) {
          try {
            await dispatch('newBotify/create', {}, { root: true });
          } catch (error) {
            const status = error?.response?.status;
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              '';
            const isDuplicateError =
              status === 409 ||
              errorMessage
                .toLowerCase()
                .includes('duplicate') ||
              errorMessage.toLowerCase().includes('conflict') ||
              errorMessage.toLowerCase().includes('already exists');

            if (isDuplicateError) {
              await dispatch('newBotify/get', null, { root: true });
            } else {
              throw error;
            }
          }
        }

        if (brand_url) {
          await dispatch(
            'newBotify/createCorpora',
            { url: brand_url },
            { root: true }
          );
        }

        commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
        if (localStorageKey) {
          localStorage.removeItem(localStorageKey);
        }
        return { success: true };
      } catch (error) {
        commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
        if (localStorageKey) {
          localStorage.removeItem(localStorageKey);
        }
        throw error;
      } finally {
        createBotInFlight = null;
      }
    })();

    createBotInFlight = promise;
    return promise;
  },

  fetchKnowledgeSources: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: true });
    try {
      const response = await OnboardingAPI.getKnowledgeSources();
      commit(types.SET_ONBOARDING_DATA, {
        step: 'knowledgeSources',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      throw error;
    }
  },

  updateKnowledgeSources: async ({ commit }, data) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
    try {
      const response = await OnboardingAPI.updateKnowledgeSources({
        has_corpora: data.has_corpora,
      });
      commit(types.SET_ONBOARDING_DATA, {
        step: 'knowledgeSources',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_STEP, 'knowledge_sources');
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },

  fetchReviewInfo: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: true });
    try {
      const response = await OnboardingAPI.getReviewInfo();
      commit(types.SET_ONBOARDING_DATA, {
        step: 'reviewInfo',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      throw error;
    }
  },

  updateReviewInfo: async ({ commit }, data) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
    try {
      const response = await OnboardingAPI.updateReviewInfo({
        brand_color: data.brand_color,
        brand_name: data.brand_name,
        bot_name: data.bot_name,
      });
      commit(types.SET_ONBOARDING_DATA, {
        step: 'reviewInfo',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_STEP, 'review_info');
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },

  fetchTestBot: async ({ commit, state: $state }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: true });
    try {
      const activityArea = $state.data.selectActivity.activity_area;
      const response = await OnboardingAPI.getTestBot(activityArea);
      commit(types.SET_ONBOARDING_DATA, {
        step: 'testBot',
        data: response.data,
      });
      commit(types.SET_ONBOARDING_STEP, 'test_bot');
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isFetching: false });
      throw error;
    }
  },

  publishBot: async ({ commit }) => {
    commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: true });
    try {
      const response = await OnboardingAPI.publishBot();
      commit(types.SET_ONBOARDING_STEP, 'publish_bot');
      commit(types.SET_ONBOARDING_UI_FLAG, {
        isUpdating: false,
        isCompleted: true,
      });
      return response.data;
    } catch (error) {
      commit(types.SET_ONBOARDING_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },

  reset: ({ commit }) => {
    commit(types.RESET_ONBOARDING);
  },
};

export const mutations = {
  [types.SET_ONBOARDING_STEP]($state, step) {
    $state.currentStep = step;
  },
  [types.SET_ONBOARDING_DATA]($state, { step, data }) {
    $state.data[step] = { ...$state.data[step], ...data };
  },
  [types.SET_ONBOARDING_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.RESET_ONBOARDING]($state) {
    $state.currentStep = null;
    $state.data = {
      selectActivity: { activity_area: null, store_types: null },
      createBot: { brand_url: null },
      knowledgeSources: { has_corpora: false },
      reviewInfo: { brand_color: null, brand_name: null, bot_name: null },
      testBot: { questions: [] },
    };
    $state.uiFlags = {
      isInitialized: false,
      isFetchingStep: false,
      isFetching: false,
      isUpdating: false,
      isCompleted: false,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
